import { defineStore } from 'pinia';
import api from '../api';

export interface RileyNotification {
  key: string;
  type: string;
  severity: 'success' | 'info' | 'warning' | 'error';
  title: string;
  body: string;
  timestamp: string;
  link: string;
  unread: boolean;
}

let pollTimer: ReturnType<typeof setInterval> | undefined;

export const useNotificationStore = defineStore('notificationStore', {
  state: () => ({
    items: [] as RileyNotification[],
    unreadCount: 0,
    lastReadAt: null as string | null,
    loading: false,
    loaded: false,
    expanded: false,
  }),
  getters: {
    latest: (state) => state.items[0] ?? null,
    hasAny: (state) => state.items.length > 0,
  },
  actions: {
    async fetch() {
      if (!this.loaded) this.loading = true;
      try {
        const res = await api.get('customer/notifications');
        const d = res.data?.data ?? {};
        this.items = Array.isArray(d.notifications) ? d.notifications : [];
        this.unreadCount = Number(d.unread_count) || 0;
        this.lastReadAt = d.last_read_at ?? null;
        this.loaded = true;
      } catch {
        // silent — a notification fetch failure must never disrupt the app
      } finally {
        this.loading = false;
      }
    },

    async markAllRead() {
      if (this.unreadCount === 0) return;
      // optimistic: clear the badge + accents immediately
      this.unreadCount = 0;
      this.items = this.items.map((n) => ({ ...n, unread: false }));
      try {
        const res = await api.post('customer/notifications/read', {});
        this.lastReadAt = res.data?.data?.last_read_at ?? this.lastReadAt;
      } catch {
        // optimistic; the next poll reconciles
      }
    },

    toggle() {
      this.expanded = !this.expanded;
      if (this.expanded) this.fetch();
    },
    open() {
      this.expanded = true;
      this.fetch();
    },
    close() {
      this.expanded = false;
    },

    startPolling(ms = 60000) {
      this.stopPolling();
      this.fetch();
      pollTimer = setInterval(() => this.fetch(), ms);
    },
    stopPolling() {
      if (pollTimer) {
        clearInterval(pollTimer);
        pollTimer = undefined;
      }
    },

    reset() {
      this.stopPolling();
      this.items = [];
      this.unreadCount = 0;
      this.lastReadAt = null;
      this.loaded = false;
      this.expanded = false;
    },
  },
});
